import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCompare } from '../context/CompareContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { X, Scale, ShoppingCart, ChevronUp, ChevronDown, Check, Minus } from 'lucide-react';
import { toast } from 'sonner';

const CompareBar = () => {
    const { compareList, removeFromCompare, clearCompare, compareCount } = useCompare();
    const [isExpanded, setIsExpanded] = useState(false);

    if (compareCount === 0) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-lg transition-all duration-300">
            {/* Toggle bar */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-center gap-2 py-2 bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                data-testid="compare-bar-toggle"
            >
                <Scale className="w-4 h-4" />
                <span className="font-medium">Сравни продукти ({compareCount})</span>
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>

            {/* Expanded content */}
            {isExpanded && (
                <div className="p-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex items-center gap-4 overflow-x-auto pb-2">
                            {compareList.map((product) => (
                                <div 
                                    key={product.product_id} 
                                    className="flex-shrink-0 w-40 bg-slate-50 dark:bg-slate-800 rounded-lg p-2 relative"
                                >
                                    <button
                                        onClick={() => removeFromCompare(product.product_id)}
                                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                                        data-testid={`remove-compare-${product.product_id}`}
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                    <img 
                                        src={product.image_url} 
                                        alt={product.name} 
                                        className="w-full h-24 object-cover rounded mb-2"
                                    />
                                    <p className="text-xs font-medium dark:text-white line-clamp-2">{product.name}</p>
                                    <p className="text-sm font-bold text-amber-600 dark:text-amber-400">
                                        {product.promotional_price || product.price}€
                                    </p>
                                </div>
                            ))}
                            
                            {/* Placeholder for empty slots */}
                            {[...Array(4 - compareCount)].map((_, i) => (
                                <div 
                                    key={`empty-${i}`} 
                                    className="flex-shrink-0 w-40 h-40 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg flex items-center justify-center"
                                >
                                    <span className="text-sm text-slate-400 dark:text-slate-500">+</span>
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                            <Button 
                                variant="outline" 
                                onClick={clearCompare}
                                className="text-red-500 border-red-300 hover:bg-red-50"
                            >
                                Изчисти всички
                            </Button>
                            <Link to="/compare">
                                <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="view-comparison-btn">
                                    <Scale className="w-4 h-4 mr-2" />
                                    Виж сравнение
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ComparePage = () => {
    const { compareList, removeFromCompare, clearCompare, compareCount } = useCompare();
    const { addToCart } = useCart();

    const handleAddToCart = async (product) => {
        const success = await addToCart(product.product_id, 1);
        if (success) {
            toast.success(`${product.name} добавен в кошницата`);
        }
    };

    if (compareCount === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white dark:from-slate-900 dark:to-slate-800 py-12">
                <div className="max-w-6xl mx-auto px-4 text-center">
                    <Scale className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                    <h1 className="text-2xl font-bold dark:text-white mb-2">Няма продукти за сравнение</h1>
                    <p className="text-slate-500 dark:text-slate-400 mb-6">
                        Добавете продукти за сравнение от каталога
                    </p>
                    <Link to="/products">
                        <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                            Разгледай продукти
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    // Get all unique attributes from products
    const allAttributes = new Set();
    compareList.forEach(product => {
        if (product.attributes) {
            Object.keys(product.attributes).forEach(key => allAttributes.add(key));
        }
    });

    const comparisonRows = [
        { key: 'image', label: 'Снимка' },
        { key: 'name', label: 'Продукт' },
        { key: 'price', label: 'Цена' },
        { key: 'category', label: 'Категория' },
        { key: 'stock', label: 'Наличност' },
        { key: 'weight', label: 'Тегло' },
        ...Array.from(allAttributes).map(attr => ({ key: `attr_${attr}`, label: attr })),
        { key: 'action', label: 'Действие' }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white dark:from-slate-900 dark:to-slate-800 py-8">
            <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold dark:text-white">Сравнение на продукти</h1>
                        <p className="text-slate-500 dark:text-slate-400">{compareCount} продукта</p>
                    </div>
                    <Button 
                        variant="outline" 
                        onClick={clearCompare}
                        className="text-red-500 border-red-300 hover:bg-red-50"
                    >
                        Изчисти всички
                    </Button>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="compare-table">
                            <thead>
                                <tr>
                                    <th className="p-4 text-left bg-slate-50 dark:bg-slate-700 font-medium text-slate-600 dark:text-slate-300 w-40">
                                        Характеристика
                                    </th>
                                    {compareList.map((product) => (
                                        <th key={product.product_id} className="p-4 text-center min-w-[200px] relative">
                                            <button
                                                onClick={() => removeFromCompare(product.product_id)}
                                                className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {comparisonRows.map((row, idx) => (
                                    <tr key={row.key} className={idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-700/50'}>
                                        <td className="p-4 font-medium text-slate-600 dark:text-slate-300 border-r border-slate-200 dark:border-slate-600">
                                            {row.label}
                                        </td>
                                        {compareList.map((product) => (
                                            <td key={product.product_id} className="p-4 text-center border-r border-slate-200 dark:border-slate-600 last:border-r-0">
                                                {row.key === 'image' && (
                                                    <Link to={`/product/${product.product_id}`}>
                                                        <img 
                                                            src={product.image_url} 
                                                            alt={product.name}
                                                            className="w-32 h-32 object-cover rounded-lg mx-auto hover:scale-105 transition-transform"
                                                        />
                                                    </Link>
                                                )}
                                                {row.key === 'name' && (
                                                    <Link to={`/product/${product.product_id}`} className="font-medium text-slate-800 dark:text-white hover:text-amber-600">
                                                        {product.name}
                                                    </Link>
                                                )}
                                                {row.key === 'price' && (
                                                    <div>
                                                        {product.promotional_price ? (
                                                            <>
                                                                <span className="text-xl font-bold text-amber-600 dark:text-amber-400">
                                                                    {product.promotional_price}€
                                                                </span>
                                                                <span className="ml-2 text-sm text-slate-400 line-through">
                                                                    {product.price}€
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <span className="text-xl font-bold text-amber-600 dark:text-amber-400">
                                                                {product.price}€
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                {row.key === 'category' && (
                                                    <Badge variant="outline">{product.category || 'Без категория'}</Badge>
                                                )}
                                                {row.key === 'stock' && (
                                                    <span className={`font-medium ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        {product.stock > 0 ? (
                                                            <span className="flex items-center justify-center gap-1">
                                                                <Check className="w-4 h-4" /> В наличност ({product.stock})
                                                            </span>
                                                        ) : (
                                                            <span className="flex items-center justify-center gap-1">
                                                                <Minus className="w-4 h-4" /> Изчерпан
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                                {row.key === 'weight' && (
                                                    <span className="text-slate-600 dark:text-slate-300">
                                                        {product.weight_visible && product.weight ? `${product.weight} ${product.weight_unit || 'кг'}` : '-'}
                                                    </span>
                                                )}
                                                {row.key.startsWith('attr_') && (
                                                    <span className="text-slate-600 dark:text-slate-300">
                                                        {product.attributes?.[row.label] || '-'}
                                                    </span>
                                                )}
                                                {row.key === 'action' && (
                                                    <Button 
                                                        onClick={() => handleAddToCart(product)}
                                                        disabled={product.stock <= 0}
                                                        className="bg-amber-500 hover:bg-amber-600 text-white"
                                                    >
                                                        <ShoppingCart className="w-4 h-4 mr-2" />
                                                        Добави
                                                    </Button>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { CompareBar, ComparePage };
